import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Button,
  Fade
} from '@material-ui/core';
import { TextField } from '@material-ui/core';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from "react-toastify";
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from "@material-ui/lab";
import NumCommandeService from '../../services/NumCommandeService';
import BonService from '../../services/BonService';
import RetourCommandeService from '../../services/RetourCommandeService';

// Icônes Material-UI
import WarningIcon from '@material-ui/icons/Warning';
import CommentIcon from '@material-ui/icons/Comment';
import BusinessIcon from '@material-ui/icons/Business';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { List } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    marginTop: theme.spacing(1),
    maxHeight: '70vh',
    overflow: 'hidden',
  },
  gridItem: {
    padding: theme.spacing(0.5),
  },
  iconAdornment: {
    color: '#226D68',
    marginRight: theme.spacing(1),
  },
  submitButton: {
    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '0.9rem',
    padding: '10px 32px',
    borderRadius: 8,
    boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
    textTransform: 'none',
    width: '100%',
    marginTop: theme.spacing(1),
    '&:hover': {
      background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
      boxShadow: '0 6px 20px rgba(220, 38, 38, 0.4)',
    },
    '&:disabled': {
      background: '#cbd5e1',
      boxShadow: 'none',
    },
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
  },
  formTitle: {
    fontWeight: 700,
    color: '#dc2626',
    marginBottom: theme.spacing(0.5),
    fontSize: '1.3rem',
  },
  formSubtitle: {
    color: '#64748b',
    fontSize: '0.8rem',
  },
  sectionTitle: {
    fontWeight: 600,
    color: '#226D68',
    marginBottom: theme.spacing(1),
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  warningSection: {
    backgroundColor: '#fef2f2',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: '1px solid #fecaca',
    marginBottom: theme.spacing(1.5),
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  infoSection: {
    backgroundColor: '#f0f9ff',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: '1px solid #e0f2fe',
    marginBottom: theme.spacing(1.5),
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  compactField: {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
    },
  },
}));

const AddRetourCommande = ({ onClose, refresh }) => {
  const classes = useStyles();
  const [numeros, setNumeros] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [selectedCommande, setSelectedCommande] = useState(null);

  const listEtats = [
    { name: "EXPIRER", description: "Produit périmé" },
    { name: "AVARIE", description: "Produit endommagé" },
    { name: "RETRAIT", description: "Retrait du marché" }
  ];

  const initialValues = {
    idNumero: '',
    quan_retour: '',
    etat: '',
    motif: '',
    produit: '',
    fournisseur: '',
    date_commande: '',
    codeIdProd: ''
  };

  const validationSchema = Yup.object().shape({
    idNumero: Yup.string().required("Numéro de commande obligatoire"),
    produit: Yup.string().required("Produit obligatoire"),
    fournisseur: Yup.string().required("Fournisseur obligatoire"),
    quan_retour: Yup.number()
      .required("Quantité de retour obligatoire")
      .positive("La quantité doit être positive")
      .min(1, "Minimum 1 unité"),
    etat: Yup.string().required("État du produit obligatoire"),
    motif: Yup.string().max(500, "Maximum 500 caractères"),
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await NumCommandeService.getNumbers();
        setNumeros(response.data || []);
      } catch (error) {
        console.error('Erreur chargement numéros:', error);
        toast.error("Erreur lors du chargement des numéros de commande");
      }
    };

    fetchData();
  }, []);

  const handleCommande = async (idNumero) => {
    try {
      const response = await BonService.getBonByNumbers(idNumero);
      setCommandes(response.data || []);
    } catch (error) {
      console.error('Erreur chargement commande:', error);
      toast.error("Erreur lors du chargement de la commande");
    }
  };

  const onSubmit = async (values, props) => {
    try {
      const retourCommande = {
        "idNumero": values.idNumero,
        "etat_produit": values.etat,
        "quan_retour": values.quan_retour,
        "motif": values.motif,
        "produit": values.produit,
        "fournisseur": values.fournisseur,
        "date_commande": commandes[0]?.dateComde || new Date().toISOString().split('T')[0],
        "codeIdProd": values.codeIdProd,
      };

      const response = await RetourCommandeService.createRetour(retourCommande);
      
      if (response?.data?.message) {
        toast.success(`✅ ${response.data.message}`, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } else {
        toast.error("❌ Erreur lors de l'enregistrement du retour", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
      
      props.resetForm();
      setCommandes([]);
      setSelectedCommande(null);
      
      // Fermer le dialog et rafraîchir si nécessaire
      if (onClose) onClose();
      if (refresh) refresh();
      
    } catch (error) {
      console.error('Erreur enregistrement retour commande:', error);
      toast.error("❌ Erreur lors de l'enregistrement du retour de commande", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  return (
    <Box>
      {/* En-tête du formulaire */}
      <Box className={classes.formHeader}>
        <WarningIcon style={{ fontSize: 32, color: '#dc2626', marginBottom: 4 }} />
        <div className={classes.formTitle}>Retour de Commande</div>
        <div className={classes.formSubtitle}>Enregistrez un retour de produit commandé</div>
      </Box>

      {/* Section d'avertissement */}
      <Box className={classes.warningSection}>
        <div className={classes.sectionTitle}>
          <WarningIcon style={{ color: '#dc2626', fontSize: 18 }} /> Attention
        </div>
        <div style={{ color: '#7f1d1d', fontSize: '0.75rem' }}>
          Cette action enregistre un retour de commande. Vérifiez bien les informations avant de valider.
        </div>
      </Box>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {(props) => (
          <Form noValidate className={classes.formContainer}>
            <Grid container spacing={1}>
              
              {/* Informations de la commande */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <FormatListNumberedIcon style={{ fontSize: 18 }} /> Informations de la commande
                </div>
              </Grid>

              {/* Numéro de commande */}
              <Grid item xs={12} className={classes.gridItem}>
                <Autocomplete
                  id="idNumero"
                  name="idNumero"
                  options={numeros}
                  getOptionLabel={option => option.numero || ''}
                  onChange={(e, v) => {
                    props.setFieldValue("idNumero", v?.idNumero || '');
                    if (v?.idNumero) {
                      handleCommande(v.idNumero);
                    } else {
                      setCommandes([]);
                    }
                  }}
                  renderInput={params => (
                    <Field 
                      as={TextField} 
                      {...params} 
                      name="idNumero" 
                      label="Numéro de commande" 
                      variant="outlined" 
                      fullWidth
                      size="small"
                      className={classes.compactField}
                      error={props.errors.idNumero && props.touched.idNumero}
                      helperText={<ErrorMessage name='idNumero' />}
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <FormatListNumberedIcon className={classes.iconAdornment} style={{ fontSize: 18 }} />
                      }}
                      placeholder="Sélectionnez un numéro de commande"
                    />
                  )}
                />
              </Grid>

              {/* Produit et Fournisseur sur la même ligne */}
              <Grid item xs={12} container spacing={1}>
                <Grid item xs={6} className={classes.gridItem}>
                  <Autocomplete
                    id="produit"
                    name="produit"
                    options={commandes}
                    getOptionLabel={option => option.nomProd || ''}
                    onChange={(e, v) => {
                      props.setFieldValue("produit", v?.nomProd || '');
                      props.setFieldValue("codeIdProd", v?.codeIdProd || '');
                      setSelectedCommande(v);
                    }}
                    renderInput={params => (
                      <Field 
                        as={TextField} 
                        {...params} 
                        name="produit" 
                        label="Produit" 
                        variant="outlined" 
                        fullWidth
                        size="small"
                        className={classes.compactField}
                        error={props.errors.produit && props.touched.produit}
                        helperText={<ErrorMessage name='produit' />}
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <List className={classes.iconAdornment} style={{ fontSize: 18 }} />
                        }}
                        disabled={!commandes.length}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6} className={classes.gridItem}>
                  <Autocomplete
                    id="fournisseur"
                    name="fournisseur"
                    options={Array.from(new Set(commandes.map(c => c.nomFournisseur))).map(fournisseur => ({ nomFournisseur: fournisseur }))}
                    getOptionLabel={option => option.nomFournisseur || ''}
                    onChange={(e, v) => {
                      props.setFieldValue("fournisseur", v?.nomFournisseur || '');
                    }}
                    renderInput={params => (
                      <Field 
                        as={TextField} 
                        {...params} 
                        name="fournisseur" 
                        label="Fournisseur" 
                        variant="outlined" 
                        fullWidth
                        size="small"
                        className={classes.compactField}
                        error={props.errors.fournisseur && props.touched.fournisseur}
                        helperText={<ErrorMessage name='fournisseur' />}
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <BusinessIcon className={classes.iconAdornment} style={{ fontSize: 18 }} />
                        }}
                        disabled={!commandes.length}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Informations de retour */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <WarningIcon style={{ fontSize: 18 }} /> Informations du retour
                </div>
              </Grid>

              {/* Quantité et État sur la même ligne */}
              <Grid item xs={12} container spacing={1}>
                <Grid item xs={6} className={classes.gridItem}>
                  <Field 
                    as={TextField} 
                    name="quan_retour" 
                    label="Quantité" 
                    fullWidth 
                    variant="outlined"
                    size="small"
                    type="number"
                    className={classes.compactField}
                    error={props.errors.quan_retour && props.touched.quan_retour}
                    helperText={<ErrorMessage name='quan_retour' />}
                    required
                    InputProps={{
                      inputProps: { min: 1, step: 1 }
                    }}
                    placeholder="Quantité"
                  />
                </Grid>

                <Grid item xs={6} className={classes.gridItem}>
                  <Autocomplete
                    id="etat"
                    name="etat"
                    options={listEtats}
                    getOptionLabel={option => option.name}
                    onChange={(e, v) => {
                      props.setFieldValue("etat", v?.name || '');
                    }}
                    renderInput={params => (
                      <Field 
                        as={TextField} 
                        {...params} 
                        name="etat" 
                        label="État" 
                        variant="outlined" 
                        fullWidth
                        size="small"
                        className={classes.compactField}
                        error={props.errors.etat && props.touched.etat}
                        helperText={<ErrorMessage name='etat' />}
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <WarningIcon className={classes.iconAdornment} style={{ fontSize: 18 }} />
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Commentaire */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <CommentIcon style={{ fontSize: 18 }} /> Commentaire
                </div>
              </Grid>

              <Grid item xs={12} className={classes.gridItem}>
                <Field 
                  as={TextField} 
                  name="motif" 
                  label="Motif du retour" 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                  className={classes.compactField}
                  error={props.errors.motif && props.touched.motif}
                  helperText={<ErrorMessage name='motif' />}
                  InputProps={{
                    startAdornment: <CommentIcon className={classes.iconAdornment} style={{ fontSize: 18 }} />
                  }}
                  placeholder="Décrivez la raison du retour (optionnel)"
                />
              </Grid>

              {/* Information sur la commande sélectionnée */}
              {selectedCommande && (
                <Grid item xs={12}>
                  <Fade in timeout={300}>
                    <Box className={classes.infoSection}>
                      <div className={classes.sectionTitle}>
                        <CalendarTodayIcon style={{ fontSize: 18 }} /> Détails de la commande
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                        <div><strong>Produit :</strong> {selectedCommande.nomProd}</div>
                        <div><strong>Fournisseur :</strong> {selectedCommande.nomFournisseur}</div>
                        <div><strong>Date :</strong> {selectedCommande.dateComde}</div>
                      </div>
                    </Box>
                  </Fade>
                </Grid>
              )}

              {/* Bouton de soumission */}
              <Grid item xs={12} className={classes.gridItem}>
                <Button 
                  type='submit' 
                  variant='contained'
                  className={classes.submitButton}
                  startIcon={<WarningIcon style={{ fontSize: 18 }} />}
                  disabled={props.isSubmitting}
                >
                  {props.isSubmitting ? 'Enregistrement...' : 'Enregistrer le retour'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default AddRetourCommande;